import type { TSportType } from "~/types";

// Record<string, TSportType> - keyed sport type constant for referencing sport-type string literals at runtime
export const SPORT_TYPE = {
  SOCCER: "soccer",
  AMERICAN_FOOTBALL: "american_football",
  OUTDOOR_FOOTBALL: "outdoor-football",
  FLAG_FOOTBALL: "flag-football",
  CANADIAN_FOOTBALL: "canadian-football",
  HANDBALL: "handball",
  HOCKEY: "hockey",
  FIELD_HOCKEY: "fieldhockey",
  ROLLER_HOCKEY: "rollerhockey",
  BASKETBALL: "basketball",
  FUTSAL: "futsal",
  RUGBY: "rugby",
  VOLLEYBALL: "volleyball",
  WRESTLING: "wrestling",
  GYMNASTICS: "gymnastics",
  BEACH_VOLLEYBALL: "beachvolleyball",
  LACROSSE: "lacrosse",
  LACROSSE_MEN: "lacrosse-men",
  LACROSSE_WOMEN: "lacrosse-women",
  BASEBALL: "baseball",
  SNOOKER: "snooker",
  SOFTBALL: "softball",
  FLOORBALL: "floorball",
  CRICKET: "cricket",
  WATER_POLO: "water-polo",
  NETBALL: "netball",
  BADMINTON: "badminton",
  BOWLING: "bowling",
  SWIMMING: "swimming",
  AUSTRALIAN_FOOTBALL: "australianFootball",
  OTHER: "other",
} as const satisfies Record<string, TSportType>;

// Compile-time exhaustiveness check: fails to compile if TSportType gains/loses a member not reflected in SPORT_TYPE's values.
type _MissingSportTypes = Exclude<TSportType, (typeof SPORT_TYPE)[keyof typeof SPORT_TYPE]>;
const _sportTypeExhaustiveCheck: _MissingSportTypes extends never ? true : _MissingSportTypes = true;

export const SPORT_TYPES: TSportType[] = [
  "basketball",
  "volleyball",
  "hockey",
  "fieldhockey",
  "soccer",
  "softball",
  "baseball",
  "lacrosse-men",
  "lacrosse-women",
  "australianFootball",
  "canadian-football",
  "outdoor-football",
  "flag-football",
  "futsal",
  "handball",
  "rugby",
  "netball",
  "water-polo",
  "wrestling",

  // Currently unsupported:
  // "american_football",
  // "rollerhockey",
  // "gymnastics",
  // "beachvolleyball",
  // "snooker",
  // "floorball",
  // "cricket",
  // "badminton",
  // "bowling",
  // "swimming",
  // "other",
];

export const B2C_SUPPORTED_SPORT_TYPES: TSportType[] = [
  "basketball",
  "volleyball",
  "hockey",
  "fieldhockey",
  "soccer",
  "lacrosse-men",
  "lacrosse-women",
  "canadian-football",
  "outdoor-football",
  "flag-football",
];

export const SPORT_POSITIONS: Partial<Record<TSportType, string[]>> = {
  "outdoor-football": ["QB", "WR", "TE", "RB", "FB", "OL", "K", "DL", "DE", "LB", "S", "CB"],
  // "Arena/Indoor Football": [ "QB", "WR", "TE", "RB", "FB", "OL", "K", "DL", "DE", "LB", "S", "CB"],
  "canadian-football": ["QB", "WR", "TE", "RB", "FB", "OL", "K", "DL", "DE", "LB", "S", "CB"],
  australianFootball: ["B", "HB", "C", "HF", "F", "Fol"],
  basketball: ["G", "F", "C"],
  hockey: ["C", "GK", "D", "W", "F"],
  lacrosse: ["GK", "D", "M", "A", "DS", "LSM", "FS"],
  soccer: ["GK", "D", "M", "F"],
  rugby: [
    "Prop",
    "Hooker",
    "Second Row",
    "Lock Forward",
    "Flanker",
    "Number Eight",
    "Scrum Halfback",
    "Fly Halfback",
    "Inside Center",
    "Outside Center",
    "Left Wing",
    "Full Back",
    "Right Wing",
  ],
  volleyball: ["OH", "MB", "S", "L", "OP", "DS"],
  baseball: ["IF", "OF", "1B", "2B", "3B", "SS", "C", "RHP", "LHP"],
  softball: ["IF", "OF", "1B", "2B", "3B", "SS", "C", "RHP", "LHP"],
  fieldhockey: ["Forward", "Defender", "Midfielder", "Goalie"],
  // "6-man Football": ["QB", "SN", "UP", "WB", "C", "G", "ME", "E", "WR", "Slot", "TB", "FB", "K", "Line", "CB", "Safety", "LB"],
  "flag-football": ["QB", "RB", "WR", "C", "DB", "Rush", "S"],
  "water-polo": ["GK", "Wings", "Driver", "Flat", "Center", "Point"],
  netball: ["GK", "GD", "WD", "WA", "C", "GS", "GA"],
  "lacrosse-men": ["GK", "D", "M", "A", "LSM", "FS"],
  "lacrosse-women": ["GK", "D", "M", "A", "DS"],
};
